import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { AuthProvider, useAuth } from './auth/AuthProvider';
import LoginPage from './auth/LoginPage';
import RegisterPage from './auth/RegisterPage';
import AlbumsPage from './albums/AlbumsPage';
import StickerGridPage from './stickers/StickerGridPage';
import FriendsPage from './friends/FriendsPage';
import SwapDetailPage from './friends/SwapDetailPage';
import MePage from './profile/MePage';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

function RequireAuth({ children }: { children: React.ReactNode }) {
  const { user, loading } = useAuth();
  if (loading) return <p className="p-6 text-muted">Lade…</p>;
  if (!user) return <Navigate to="/login" replace />;
  return <>{children}</>;
}

function AppShell({ children }: { children: React.ReactNode }) {
  // Ambient Stadium-Glow aus dem Visual System — zwei radiale Gradients als atmosphärischer Schein.
  return (
    <div className="relative min-h-full overflow-x-hidden">
      <div className="dk-stadium-glow" aria-hidden="true" />
      <div className="relative z-[1] min-h-full">{children}</div>
    </div>
  );
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <BrowserRouter>
          <AppShell>
            <Routes>
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />

              <Route path="/" element={<Navigate to="/albums" replace />} />
              <Route path="/albums" element={<RequireAuth><AlbumsPage /></RequireAuth>} />
              <Route path="/albums/:slug" element={<RequireAuth><StickerGridPage /></RequireAuth>} />
              <Route path="/albums/:slug/friends" element={<RequireAuth><FriendsPage /></RequireAuth>} />
              <Route path="/albums/:slug/friends/:friendId" element={<RequireAuth><SwapDetailPage /></RequireAuth>} />
              <Route path="/me" element={<RequireAuth><MePage /></RequireAuth>} />

              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </AppShell>
        </BrowserRouter>
      </AuthProvider>
    </QueryClientProvider>
  );
}
